import { observer } from 'mobx-react';
import * as React from 'react';
import { SelectBox } from '@components/select-box';
import { TextField } from '@components/textfield';
import { AggregationAlgorithm } from '@core/api/ChallengeClient';
import { injectTSDI } from '@core/tsdi';
import { ReactComponent as HighScore } from '../../../../../../../assets/svg/icons/badge.svg';
import { ReactComponent as FinishIcon } from '../../../../../../../assets/svg/icons/finish.svg';
import { ChallengeDataStore } from '../../data-store';
import { BoxItem } from '../challenge-type';
import { RuleSetSection } from '../rule-set-section';
import { ChallengeRulesStore } from '../store';
import { TeamRulesStore } from './store';

const TeamRules = observer(() => {
    const { disabledRulesEdit } = injectTSDI(ChallengeDataStore);
    const {
        fields: { teamRules },
        validateRules
    } = injectTSDI(ChallengeRulesStore);
    const {
        fields: {
            pointsAlgorithm,
            winnersCount,
            minimumTeamSize,
            teamRulesEnabled
        },
        form
    } = injectTSDI(TeamRulesStore);

    return (
        <RuleSetSection
            title="Team Rules"
            enabled={teamRules.value}
            disabled={disabledRulesEdit}
            toggleTestId="challenge-team-rules"
            error={Boolean(teamRules.error)}
            onToggle={(checked) => {
                teamRules.onChange(checked);
                teamRulesEnabled.onChange(checked);
                validateRules();
            }}
        >
            <SelectBox
                label="Event type"
                options={[
                    {
                        label: 'Average',
                        value: AggregationAlgorithm.AVERAGE,
                        component: ({ label }) => (
                            <BoxItem
                                icon={HighScore}
                                label={label}
                                viewBox="-1 0 24 24"
                                dataTestId="challenge-activity-highscore"
                            />
                        )
                    },
                    {
                        label: 'Sum',
                        value: AggregationAlgorithm.SUM,
                        component: ({ label }) => (
                            <BoxItem
                                icon={FinishIcon}
                                label={label}
                                viewBox="-1 0 24 24"
                                dataTestId="challenge-activity-race"
                            />
                        )
                    }
                ]}
                value={pointsAlgorithm.value}
                onChange={(value: AggregationAlgorithm) => {
                    pointsAlgorithm.onChange(value);
                    validateRules();
                    form.validate();
                }}
                error={pointsAlgorithm.error}
                helperText={
                    pointsAlgorithm.value === AggregationAlgorithm.AVERAGE
                        ? 'Counts the average of winning teams'
                        : 'Counts the sum of earned by all team members'
                }
                disabled={disabledRulesEdit}
                required
            />
            <div className="grid gap-4 md:grid-cols-2">
                <TextField
                    type="number"
                    label="Winners count"
                    value={winnersCount.value || ''}
                    onChange={(value) => {
                        winnersCount.onChange(Number(value));
                        validateRules();
                    }}
                    error={winnersCount.error}
                    InputLabelShrink
                    data-testid="challenge-team-winners-count-field"
                    disabled={disabledRulesEdit}
                />
                <TextField
                    type="number"
                    label="Minimum team size"
                    value={
                        typeof minimumTeamSize.value === 'number'
                            ? minimumTeamSize.value
                            : ''
                    }
                    error={minimumTeamSize.error}
                    onChange={(value) => {
                        minimumTeamSize.onChange(
                            value.toLocaleString().length === 0
                                ? undefined
                                : Number(value)
                        );
                        validateRules();
                    }}
                    InputLabelShrink
                    data-testid="challenge-team-minimum-size-field"
                    disabled={disabledRulesEdit}
                />
            </div>
        </RuleSetSection>
    );
});

export default TeamRules;
